import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { Alert, Button, FormField, Input } from '@playerbase/ui';
import { ResetPasswordFormSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/use-auth.js';
import { AuthShell } from './auth-shell.js';

export function ResetPasswordPage(): JSX.Element {
  const { status } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // AuthCallbackPage handles exchangeCodeForSession. By the time the user
  // lands here the session should already exist. If it does not the link is
  // invalid or expired.
  if (status === 'loading') {
    return (
      <AuthShell title="Reset your password">
        <p className="text-sm text-text-muted">Verifying…</p>
      </AuthShell>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <AuthShell title="Link expired" subtitle="This password reset link is invalid or has already been used.">
        <div className="flex flex-col gap-4">
          <Alert tone="danger">This link is invalid or expired. Please request a new reset link.</Alert>
          <Link to="/sign-in">
            <Button variant="primary" className="w-full">Back to sign in</Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setPasswordError('');
    setConfirmError('');
    setServerError('');

    const result = ResetPasswordFormSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      for (const issue of result.error.issues) {
        if (issue.path[0] === 'password') setPasswordError(issue.message);
        if (issue.path[0] === 'confirmPassword') setConfirmError(issue.message);
      }
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: result.data.password });
      if (error) {
        setServerError(error.message);
      } else {
        navigate('/', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4" noValidate>
        {serverError !== '' && (
          <Alert tone="danger">{serverError}</Alert>
        )}

        <FormField error={passwordError !== '' ? passwordError : undefined}>
          <FormField.Label>New password</FormField.Label>
          <FormField.Control>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full"
            />
          </FormField.Control>
          {passwordError !== '' && <FormField.ErrorText>{passwordError}</FormField.ErrorText>}
        </FormField>

        <FormField error={confirmError !== '' ? confirmError : undefined}>
          <FormField.Label>Confirm password</FormField.Label>
          <FormField.Control>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full"
            />
          </FormField.Control>
          {confirmError !== '' && <FormField.ErrorText>{confirmError}</FormField.ErrorText>}
        </FormField>

        <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
          {submitting ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthShell>
  );
}
